import _ScrapbookViewQuery from "../__generated__/ScrapbookViewQuery.graphql";
import { ChevronLeft, ChevronRight, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { claim, isCurrent } from '../audio/oneVoice.ts';
import { CharacterFace, usePhase } from '../avatar/CharacterFace';
import { En } from '../i18n/En';
import { getLocale, t } from '../i18n/t';
import { registerFor } from '../profile/register';
import { dayWord, pageView, startIndex, swipeStep, turn, type PageView } from '../scrapbook/scrapbook.ts';
import { Sticker } from '../scrapbook/Sticker';
import type { ScrapbookViewQuery as ScrapbookViewQueryType } from '../__generated__/ScrapbookViewQuery.graphql';

// `first: 100` is the server's `MAX_PAGE_SIZE`: a page a celebration links to
// is the newest, so it is always within reach.
const ScrapbookQuery = (_ScrapbookViewQuery.hash && _ScrapbookViewQuery.hash !== "c19a1028475340ca59457d38a3afd0ff" && console.error("The definition of 'ScrapbookViewQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ScrapbookViewQuery);

/**
 * The child's own recording (`GET /audio/{turn_id}`), through a plain
 * `<audio>` element. Claims the page's one voice as `HearIt` does, so it
 * never plays over the tutor. A recording that was never stored answers 404,
 * which says so in place rather than leaving a button that does nothing.
 */
export function HearMyself({
  url
}: {
  url: string;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => () => audio.current?.pause(), []);
  const play = () => {
    const el = audio.current;
    if (!el || !claim(el)) return;
    setFailed(false);
    el.currentTime = 0;
    void el.play().catch(() => {
      if (isCurrent(el)) setFailed(true);
    });
  };
  return <div className="flex flex-col items-center gap-1">
      <audio ref={audio} src={url} preload="none" onError={() => setFailed(true)} />
      <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" onClick={play}>
        <Volume2 aria-hidden className="size-5" />
        {t('scrapbook-hear-it')}
      </button>
      {failed && <p role="alert" className="text-base-content/70 text-sm">
          {t('scrapbook-hear-it-failed')}
        </p>}
    </div>;
}
function Page({
  page,
  now,
  phase
}: {
  page: PageView;
  now: Date;
  phase: number;
}) {
  return <article className="bg-base-100 rounded-box flex flex-col items-center gap-4 p-6 text-center shadow-lg">
      <Sticker slug={page.sticker} named className="size-40" />
      <div className="flex items-center gap-3">
        {page.character && <span role="img" aria-label={t('scrapbook-with', {
        name: page.character.displayName
      })} className="block w-12 shrink-0">
            <CharacterFace artKey={page.character.artKey} accentHex={page.character.accentHex} intensity={page.character.intensity} expression="neutral" phase={phase} />
          </span>}
        <p className="text-xl font-bold">{dayWord(page.createdAt, now, getLocale())}</p>
      </div>
      {page.sentence && <div className="flex flex-col items-center gap-2">
          <p className="text-base-content/70">{t('scrapbook-you-said')}</p>
          <p className="text-2xl font-semibold">
            <En>{page.sentence}</En>
          </p>
          {page.audioUrl && <HearMyself url={page.audioUrl} />}
        </div>}
    </article>;
}

/**
 * The scrapbook (kid memory design §3.3): one page per screen, newest first,
 * turned by swipe or by the arrows. No page numbers and no totals, on
 * purpose -- a count is a score, and a child with three pages beside a
 * sibling with thirty should see three good pages, not a short tally.
 *
 * Kid register only. An adult who reaches the URL is put on Progress rather
 * than shown an error: nothing went wrong, there is simply nothing here for
 * them.
 */
export function ScrapbookView() {
  const [now] = useState(() => new Date());
  const phase = usePhase();
  const [params] = useSearchParams();
  const data = useLazyLoadQuery<ScrapbookViewQueryType>(ScrapbookQuery, {}, {
    fetchPolicy: 'store-and-network'
  });
  const learner = data.me?.learner;
  const pages = (learner?.scrapbook ?? []).map(pageView);
  const [index, setIndex] = useState(() => startIndex(pages, params.get('page')));
  const dragFrom = useRef<number | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  if (registerFor(learner?.defaultCharacter?.audience) !== 'kid') return <Navigate to="/history" replace />;
  const heading = <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold outline-none">
      {t('scrapbook-title')}
    </h1>;
  if (pages.length === 0) {
    return <div className="mx-auto flex max-w-md flex-col items-center gap-6 py-6 text-center">
        {heading}
        <Sticker slug="book" className="size-32 opacity-60" />
        <p className="text-lg">{t('scrapbook-empty')}</p>
        <Link to="/session" className="btn btn-primary btn-lg min-h-(--hit-min)">
          {t('session-new')}
        </Link>
      </div>;
  }

  // Clamped here rather than when set: `store-and-network` can answer with
  // fewer pages than the cached list the index was chosen from.
  const at = Math.min(index, pages.length - 1);
  const go = (step: number) => setIndex(turn(at, step, pages.length).index);
  const arrow = (step: number) => {
    const {
      index: to,
      edge
    } = turn(at, step, pages.length);
    setIndex(to);
    if (edge) headingRef.current?.focus();
  };
  return <div className="mx-auto flex max-w-md flex-col gap-4 py-6">
      {heading}
      <div
    // `pan-y`, so a horizontal drag reaches these handlers instead of
    // scrolling, while the page still scrolls vertically on a phone.
    className="touch-pan-y select-none"
    // The page's content is announced when it turns; the wrapper rather
    // than the article, which is remounted per page and so would be a
    // new live region each time, never a changed one.
    aria-live="polite" onPointerDown={e => {
      dragFrom.current = e.clientX;
      // Captured so a mouse drag released off the page still ends here.
      // Not from a button: a captured pointer's click goes to the
      // capturing element, and hear-it would never hear its own tap.
      if (!(e.target instanceof Element && e.target.closest('button'))) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }} onPointerUp={e => {
      if (dragFrom.current === null) return;
      const step = swipeStep(e.clientX - dragFrom.current);
      dragFrom.current = null;
      if (step !== 0) go(step);
    }} onPointerCancel={() => {
      dragFrom.current = null;
    }}>
        <Page key={pages[at].id} page={pages[at]} now={now} phase={phase} />
      </div>
      <div className="flex justify-between">
        <button type="button" className="btn btn-circle btn-lg min-h-(--hit-min)" aria-label={t('scrapbook-previous')} disabled={at === 0} onClick={() => arrow(-1)}>
          <ChevronLeft aria-hidden className="size-8" />
        </button>
        <button type="button" className="btn btn-circle btn-lg min-h-(--hit-min)" aria-label={t('scrapbook-next')} disabled={at === pages.length - 1} onClick={() => arrow(1)}>
          <ChevronRight aria-hidden className="size-8" />
        </button>
      </div>
    </div>;
}